'use client';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Box } from '@mui/material';
import { Grid } from '@mui/material';
import { useTheme } from '@mui/material';
import Typography from '@mui/material/Typography';
import Image from 'next/image';
import React from 'react';

import { CustomBackground } from '@/components/CustomBackground/CustomBackground';
import { StrapiBackgroundOptions, StrapiDisplayOptions } from '@/lib/DisplayOptionsHelper';
import { getImageUrl } from '@/lib/helpers/getImageUrl';
import {
  ComponentBlocksFaqList,
  ComponentBlocksQuestionList,
  Enum_Componentshareddisplayoptions_Contentswidth,
} from '@/types';

import GlobalBlockRenderer from '../GlobalBlockRenderer/GlobalBlockRenderer';
import { FaqListWithGrouping } from './FaqListWithGrouping';

export function FaqListPlain({
  props,
}: {
  props: ComponentBlocksFaqList;
  spacingY?: number | { pt?: number; pb?: number };
}) {
  if (props.Variant === 'WithGrouping') {
    return <FaqListWithGrouping props={props} />;
  }

  const faqContent = (
    <Box>
      {props.Questions?.map((q, i) => (
        <QuestionAnswer key={i} {...(q as ComponentBlocksQuestionList)} />
      ))}
    </Box>
  );

  return (
    <CustomBackground
      backgroundOptions={(props?.FaqListBackground ?? undefined) as StrapiBackgroundOptions}
      displayOptions={
        {
          ...props?.FaqListDisplayOptions,
          ContentsWidth:
            props?.FaqListDisplayOptions?.ContentsWidth ??
            Enum_Componentshareddisplayoptions_Contentswidth.Wide,
          PaddingTop: props?.FaqListDisplayOptions?.PaddingTop ?? 'xs',
          PaddingBottom: props?.FaqListDisplayOptions?.PaddingBottom ?? 'xs',
          PaddingInline: props?.FaqListDisplayOptions?.PaddingInline ?? 'xs',
        } as StrapiDisplayOptions
      }
      boxProps={{
        // @ts-expect-error - this does work, but types are not correct
        'data-component': 'FaqListPlain',
      }}
    >
      {props.FaqListImage ? (
        <>
          {/* Heading on top */}
          {props.Body && (
            <Box
              sx={{
                '& h2:first-of-type, & p, & h3, & h4': {
                  textAlign: 'center',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                },
              }}
            >
              <GlobalBlockRenderer content={props.Body || []} forceHeadingLevel={2} />
            </Box>
          )}

          <Grid container spacing={6} alignItems="flex-start">
            {/* LEFT COLUMN → Image */}
            <Grid item xs={12} md={5}>
              <Box sx={{ textAlign: 'center' }}>
                <Image
                  src={getImageUrl({ image: props.FaqListImage, size: 'url' })!}
                  alt={props.FaqListImage.alternativeText || 'FAQ illustration'}
                  width={props.FaqListImage.width || 500}
                  height={props.FaqListImage.height || 400}
                  style={{ maxWidth: '100%', height: 'auto', borderRadius: 2 }}
                />
              </Box>
            </Grid>

            {/* RIGHT COLUMN → FAQs */}
            <Grid item xs={12} md={7}>
              {faqContent}
            </Grid>
          </Grid>
        </>
      ) : (
        <Grid container spacing={6} alignItems="flex-start">
          {/* LEFT COLUMN → Body instead of image */}
          <Grid item xs={12} md={5}>
            {props.Body && (
              <Box
                sx={{
                  '& h2:first-of-type, & p, & h3, & h4': {
                    textAlign: { xs: 'center', md: 'left' },
                    marginLeft: { xs: 'auto', md: 0 },
                    marginRight: { xs: 'auto', md: 0 },
                  },
                }}
              >
                <GlobalBlockRenderer content={props.Body || []} forceHeadingLevel={2} />
              </Box>
            )}
          </Grid>

          {/* RIGHT COLUMN → FAQs */}
          <Grid item xs={12} md={7}>
            {faqContent}
          </Grid>
        </Grid>
      )}
    </CustomBackground>
  );
}

function QuestionAnswer(props: ComponentBlocksQuestionList) {
  const theme = useTheme();

  return (
    <Accordion sx={(theme) => ({ borderBottom: `solid 1px ${theme.palette.divider}` })}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ fontSize: 30, color: theme.palette.common.black }} />}
        aria-controls={`panel-${props.id}-content`}
        id={`panel-${props.id}`}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {props.Question}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <GlobalBlockRenderer content={props.Answer || []} />
      </AccordionDetails>
    </Accordion>
  );
}
